import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { Switch } from "../components/Switch";
import {
  FLAG_TEMPLATES,
  fetchMobileClientFlags,
  MOBILE_FLAG_BLURB,
  MOBILE_FLAG_COPY,
  MOBILE_FLAG_GROUPS,
  upsertAccountMobileFeatureFlags,
  type MobileFlagDraft,
  type MobileFlagKey,
  type MobileFlagTemplate,
} from "../data/operator-controls";
import { useReadModel } from "../data/useReadModel";

// Platform cockpit mobile flag editor (PRD-001P): the full 11-key catalog grouped by domain,
// one-click templates with a confirm step, and an "effective on next foreground" read-back that
// mirrors get_mobile_client_flags after every save. Writes go through the lock-aware hosted RPC.
export function PlatformMobileFlagControls({
  client,
  accountId,
  canManage,
}: {
  client: SupabaseClient;
  accountId: string;
  canManage: boolean;
}) {
  const [reloadNonce, setReloadNonce] = useState(0);
  const { status, data, error } = useReadModel<MobileFlagDraft>(
    () => fetchMobileClientFlags(client, accountId),
    [client, accountId, reloadNonce],
    {},
  );

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading mobile flags…</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load mobile feature flags."}
      </p>
    );
  }
  if (data === null) {
    return null;
  }

  return (
    <div className="cockpit-flags">
      <EffectiveReadback flags={data} />
      <FlagEditor
        client={client}
        accountId={accountId}
        flags={data}
        canManage={canManage}
        onSaved={() => setReloadNonce((nonce) => nonce + 1)}
      />
    </div>
  );
}

function EffectiveReadback({ flags }: { flags: MobileFlagDraft }) {
  const onKeys = (Object.keys(flags) as MobileFlagKey[]).filter((key) => flags[key]);
  return (
    <div className="cockpit-readback" role="status" aria-label="Effective mobile flags on next foreground">
      <span className="cockpit-readback__label">On next foreground</span>
      <span className="cockpit-readback__count">{onKeys.length} / 11 on</span>
      <span className="cockpit-readback__list">
        {onKeys.length === 0 ? "Nothing enabled" : onKeys.map((key) => MOBILE_FLAG_COPY[key]).join(" · ")}
      </span>
    </div>
  );
}

function FlagEditor({
  client,
  accountId,
  flags,
  canManage,
  onSaved,
}: {
  client: SupabaseClient;
  accountId: string;
  flags: MobileFlagDraft;
  canManage: boolean;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<MobileFlagDraft>(flags);
  const [pendingTemplate, setPendingTemplate] = useState<MobileFlagTemplate | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);

  // A fresh server read (account switch or post-save reload) resets the working draft.
  useEffect(() => {
    setDraft(flags);
    setPendingTemplate(null);
  }, [flags]);

  function setFlag(key: MobileFlagKey, value: boolean): void {
    setDraft((current) => ({ ...current, [key]: value }));
    setFailure(null);
    setReceipt(null);
  }

  async function persist(next: MobileFlagDraft): Promise<void> {
    if (busy || !canManage) return;
    setBusy(true);
    setFailure(null);
    setReceipt(null);
    const outcome = await upsertAccountMobileFeatureFlags(client, accountId, next);
    if (outcome.ok) {
      setReceipt("Mobile flags saved.");
      onSaved();
    } else {
      setFailure(outcome.reason);
    }
    setBusy(false);
  }

  function applyTemplate(template: MobileFlagTemplate): void {
    setDraft({ ...template.flags });
    setPendingTemplate(null);
    void persist({ ...template.flags });
  }

  return (
    <div className="operator-panel cockpit-flags__panel">
      {canManage ? (
        <div className="cockpit-templates" aria-label="Flag templates">
          <span className="cockpit-templates__label">Templates</span>
          {FLAG_TEMPLATES.map((template) => (
            <button
              key={template.id}
              type="button"
              className="cockpit-templates__btn"
              disabled={busy}
              onClick={() => {
                setPendingTemplate(template);
                setFailure(null);
                setReceipt(null);
              }}
            >
              {template.label}
            </button>
          ))}
        </div>
      ) : null}

      {pendingTemplate !== null ? (
        <div className="cockpit-confirm" role="alertdialog" aria-label="Confirm template apply">
          <p className="cockpit-confirm__copy">
            Apply <strong>{pendingTemplate.label}</strong> to this account? {pendingTemplate.description}
          </p>
          <div className="cockpit-confirm__actions">
            <button
              type="button"
              className="minter__confirm-go"
              disabled={busy}
              onClick={() => applyTemplate(pendingTemplate)}
            >
              {busy ? "Applying…" : `Apply ${pendingTemplate.label}`}
            </button>
            <button
              type="button"
              className="cockpit-confirm__cancel"
              disabled={busy}
              onClick={() => setPendingTemplate(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {MOBILE_FLAG_GROUPS.map((group) => (
        <section key={group.id} className="cockpit-group" aria-label={`${group.label} flags`}>
          <h4 className="cockpit-group__title">{group.label}</h4>
          <ul className="operator-list">
            {group.keys.map((key) => (
              <li key={key} className="operator-row cockpit-row">
                <div className="cockpit-row__copy">
                  <span className="operator-row__title">
                    {MOBILE_FLAG_COPY[key]}
                    <span className={`rollout__badge ${draft[key] ? "rollout__badge--ready" : "rollout__badge--disabled"}`}>
                      {draft[key] ? "On" : "Off"}
                    </span>
                  </span>
                  <span className="cockpit-row__blurb">{MOBILE_FLAG_BLURB[key]}</span>
                </div>
                <Switch
                  checked={draft[key]}
                  disabled={!canManage || busy}
                  layout="compact"
                  label="Account default"
                  onChange={(value) => setFlag(key, value)}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}

      {canManage ? (
        <div className="policy__editor-actions">
          <button type="button" className="minter__confirm-go" disabled={busy} onClick={() => void persist(draft)}>
            {busy ? "Saving…" : "Save mobile flags"}
          </button>
        </div>
      ) : (
        <p className="page__note">Read-only for this role.</p>
      )}
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}
    </div>
  );
}
